import { useEffect, useState } from "react";
import AccountButton from "./AccountButton";
import AccountInput from "./AccountInput";
import AccountSection from "./AccountSection";
import {
  changePassword,
  getAccountDetails,
  updateAccount,
} from "./api/apiFunctions";
import { t } from "./i18n";
import { toast } from "react-toastify";

export default function AccountPage({ lang = "en", goToTab }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    ssn: "",
    memberId: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear success message when user starts typing
    if (success) {
      setSuccess(null);
    }
    // Clear field-specific error when user starts typing
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: null }));
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch account details on component mount
  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Get token from localStorage
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        // Fetch account data from API
        const response = await getAccountDetails(token);

        if (response.success && response.data) {
          setForm({
            fullName: response.data.fullName || "",
            email: response.data.email || "",
            ssn: response.data.ssn || "",
            memberId: response.data.memberId || "",
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        } else {
          throw new Error("Failed to fetch account data");
        }
      } catch (error) {
        console.error("Error fetching account data:", error);
        // setErrors({
        //   fullName:
        //     error.message || "Failed to load account data. Please try again.",
        // });
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, []);

  const handleSave = async () => {
    // Check if password fields are being changed (declare outside try block)
    const isChangingPassword =
      form.newPassword || form.confirmPassword || form.currentPassword;

    try {
      setIsSaving(true);
      setErrors({});
      setSuccess(null);

      // Get token from localStorage
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Validate password fields if any are filled
      if (isChangingPassword) {
        const passwordErrors = {};

        if (!form.currentPassword) {
          passwordErrors.currentPassword =
            "Current password is required to change password";
        }
        if (form.newPassword && form.newPassword.length < 6) {
          passwordErrors.newPassword =
            "New password must be at least 6 characters long";
        }
        if (
          form.newPassword &&
          form.confirmPassword &&
          form.newPassword !== form.confirmPassword
        ) {
          passwordErrors.confirmPassword = "New passwords do not match";
        }

        if (Object.keys(passwordErrors).length > 0) {
          setErrors(passwordErrors);
          return false; // Return false to indicate failure
        }
      }

      // Prepare account data for update (excluding password fields)
      const accountData = {
        fullName: form.fullName,
        email: form.email,
        ssn: form.ssn,
      };

      // Update account information
      const accountResponse = await updateAccount(accountData, token);

      console.log("Account response", accountResponse);

      if (!accountResponse.success) {
        // Handle specific account update errors
        const accountErrors = {};
        const message =
          accountResponse.message || "Failed to update account information";

        if (message.toLowerCase().includes("email")) {
          accountErrors.email = message;
        } else if (
          message.toLowerCase().includes("name") ||
          message.toLowerCase().includes("fullname")
        ) {
          accountErrors.fullName = message;
        } else if (
          message.toLowerCase().includes("ssn") ||
          message.toLowerCase().includes("social security")
        ) {
          accountErrors.ssn = message;
        } else {
          // If we can't determine the specific field, show on email as default
          accountErrors.email = message;
        }

        setErrors(accountErrors);
        return false; // Return false to indicate failure
      }

      // Change password if password fields are filled
      if (isChangingPassword) {
        const passwordData = {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          confirmNewPassword: form.confirmPassword,
        };

        const passwordResponse = await changePassword(passwordData, token);

        if (!passwordResponse.success) {
          // Handle specific password errors
          const passwordErrors = {};
          const message =
            passwordResponse.message || "Failed to change password";

          if (
            message.toLowerCase().includes("current password") ||
            message.toLowerCase().includes("incorrect") ||
            message.toLowerCase().includes("wrong") ||
            message.toLowerCase().includes("invalid")
          ) {
            passwordErrors.currentPassword = message;
          } else if (
            message.toLowerCase().includes("new password") ||
            message.toLowerCase().includes("password too short") ||
            message.toLowerCase().includes("password requirements")
          ) {
            passwordErrors.newPassword = message;
          } else if (
            message.toLowerCase().includes("confirm") ||
            message.toLowerCase().includes("match") ||
            message.toLowerCase().includes("do not match")
          ) {
            passwordErrors.confirmPassword = message;
          } else {
            // For any password-related error, default to current password field
            passwordErrors.currentPassword = message;
          }

          setErrors(passwordErrors);
          return false; // Return false to indicate failure
        }
      }

      // Clear password fields after successful save
      setForm((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      // Show success message
      setSuccess("Account updated successfully!");
      return true; // Return true to indicate success
    } catch (error) {
      // Show general errors based on context
      const errorMessage =
        error.message || "Failed to save account. Please try again.";
      const generalErrors = {};

      // If we were trying to change password, show error on current password field
      if (isChangingPassword) {
        generalErrors.currentPassword = errorMessage;
      } else {
        // Otherwise show on fullName field
        toast.error(errorMessage);
      }

      setErrors(generalErrors);
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8">
        <div className="w-full max-w-3xl">
          <div className="bg-white rounded-xl shadow p-8 mt-4">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4962d8]"></div>
              <span className="ml-4 text-lg text-[#2d348f]">
                Loading account details...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-4">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <AccountSection>{t("account", lang)}</AccountSection>
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="text-green-600 text-sm">✅ {success}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <AccountInput
                label={t("fullName", lang) || "Full Name"}
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
              />
              {errors.fullName && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.fullName}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("email", lang) || "Email"}
                name="email"
                value={form.email}
                onChange={handleChange}
              />
              {errors.email && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.email}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("ssn", lang) || "Social Security"}
                name="ssn"
                value={form.ssn}
                onChange={handleChange}
                mask="000-00-0000"
              />
              {errors.ssn && (
                <div className="text-red-500 text-sm mt-1">⚠️ {errors.ssn}</div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("memberId", lang) || "SAL Member ID"}
                name="memberId"
                value={form.memberId}
                disabled
              />
              {errors.memberId && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.memberId}
                </div>
              )}
            </div>
          </div>
          <AccountSection>
            {t("changePassword", lang) || "Change Password"}
          </AccountSection>
          <div className="grid grid-cols-1 gap-4 mb-8">
            <div>
              <AccountInput
                label={t("currentPassword", lang) || "Current Password"}
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                type="password"
                showPasswordToggle={true}
              />
              {errors.currentPassword && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.currentPassword}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("newPassword", lang) || "New Password"}
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                type="password"
                showPasswordToggle={true}
              />
              {errors.newPassword && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.newPassword}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("confirmNewPassword", lang) || "Confirm New Password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                type="password"
                showPasswordToggle={true}
              />
              {errors.confirmPassword && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.confirmPassword}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <AccountButton
              className="md:w-1/3"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <span className="ml-2">Next &raquo;</span>
            </AccountButton>
          </div>
        </div>
      </div>
    </div>
  );
}
